<script setup lang="ts">
import { api } from '@/api';
import {
  ActivateNewCalibrationDataRequest,
  CalibrationDataDto,
  PtwCloudCalibrationDataDto,
} from '@/api/generator';
import Dialog from '@/components/Dialog/Dialog.vue';
import { useVeeForm } from '@/components/forms/useVeeForm.ts';
import Notification from '@/components/notifications/Notification.vue';
import { NotificationMessage } from '@/components/notifications/types.ts';
import { formBackendErrorHandler, stopErrorPropagation } from '@/errorHandling';
import { useStoreErrorNotification } from '@/errorHandling/useStoreErrorNotification.ts';
import { routenames } from '@/router/routenames.ts';
import { useNotificationStore } from '@/stores/notificationStore.ts';
import CalibrationInformation from '@/views/qaEquipment/components/CalibrationInformation.vue';
import { reloadEquipmentKey } from '@/views/qaEquipment/types.ts';
import { useTranslation } from 'i18next-vue';
import { inject, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import Button from '@/components/buttons/Button.vue';

const modelValue = defineModel<boolean>({ type: Boolean, default: false });

type QaEquipmentActivateNewCalibrationDialogProps = {
  id: string;
  newCalibrationData: PtwCloudCalibrationDataDto;
  currentCalibrationData?: CalibrationDataDto;
};
const props = defineProps<QaEquipmentActivateNewCalibrationDialogProps>();

type QaEquipmentActivateNewCalibrationDialogEmits = {
  'click:contactSupport': [];
};
defineEmits<QaEquipmentActivateNewCalibrationDialogEmits>();

const { success: notifySuccess } = useNotificationStore();
const { t } = useTranslation();

const router = useRouter();
const { currentRoute } = router;
const reloadEquipment =
  currentRoute.value.name === routenames.qaEquipment.add.configuration
    ? undefined
    : inject(reloadEquipmentKey);

const { form } = useVeeForm<ActivateNewCalibrationDataRequest>({
  mode: 'edit',
  options: {
    initialValues: {
      id: props.id,
    },
  },
});

const { handleSubmit } = form;
const storeNotification = ref<NotificationMessage | undefined>(undefined);
const { errorHandler, clearError } = useStoreErrorNotification({
  showNotificationInToast: true,
  notificationMessage: storeNotification,
  closable: false,
});
const submit = handleSubmit(async (activateNewCalibrationDataRequest) => {
  clearError();
  const { success } = await api.qaEquipmentApi.activateNewCalibrationData(
    activateNewCalibrationDataRequest,
    [formBackendErrorHandler(form), errorHandler, stopErrorPropagation],
  );
  if (success) {
    reloadEquipment?.();
    modelValue.value = false;
    notifySuccess(t('ActivateNewCalibrationCertificateSuccessMsg'));
  }
});

watch(modelValue, (valueNew) => {
  if (valueNew) {
    clearError();
  }
});
</script>

<template>
  <Dialog v-model="modelValue" hideClose size="large">
    <template #title>{{ $t('ActivateNewCalibrationCertificate') }}</template>
    <template #text>
      <template v-if="currentCalibrationData">
        <h4>{{ $t('CurrentCalibrationCertificate') }}</h4>
        <v-row>
          <v-col>
            <CalibrationInformation
              :calibrationData="currentCalibrationData"
              hideActivationDate
              colorizeState
              expanded
            />
          </v-col>
        </v-row>
      </template>
      <h4 :class="{ 'mt-6': currentCalibrationData }">{{ $t('NewCalibrationCertificate') }}</h4>
      <v-row>
        <v-col>
          <CalibrationInformation
            :calibrationData="newCalibrationData"
            hideActivationDate
            expanded
          />
        </v-col>
      </v-row>
      <v-alert
        class="mt-12"
        type="warning"
        :text="$t('NewCalibrationCertificateAllReferenceMeasurementsMustBeRenewedMsg')"
        variant="tonal"
      />
      <Notification class="mt-5" :value="storeNotification" />

      <Button
        v-if="storeNotification?.type === 'error'"
        variant="secondary"
        class="mt-8"
        :label="$t('ContactPtwCustomerSupport')"
        @click="$emit('click:contactSupport')"
      />
    </template>
    <template #actions>
      <Button variant="text" :label="$t('Cancel')" @click="modelValue = false" />
      <Button type="submit" variant="primary" :label="$t('ActivateNow')" @click="submit" />
    </template>
  </Dialog>
</template>
